import React from "react";

/**
 * Shared utility to format log text with:
 * - @N-NAME mentions → bold white uppercase (name only, no prefix)
 * - Camera references (&a01, &amp;l02, &cam b03, cam c04) → orange badges (CAM A01)
 * - [BRACKETS] → bold white uppercase (existing behavior preserved)
 * - Inline participant name detection → bold white uppercase
 * - Inline "N-name" prefix stripping in plain text
 */

// Camera badge style (solid orange, matches LogCard style)
const camBadgeStyle: React.CSSProperties = {
  background: "rgba(250, 204, 21, 0.15)",
  color: "#FACC15",
  padding: "1px 6px",
  borderRadius: "4px",
  fontSize: "0.75em",
  fontWeight: 700,
  letterSpacing: "0.5px",
  whiteSpace: "nowrap",
  display: "inline-block",
  verticalAlign: "middle",
  margin: "0 2px",
};

// Name highlight style
const nameStyle: React.CSSProperties = {
  fontWeight: 700,
  color: "#fff",
};

// Sentence case conversion
export const toSentenceCase = (t: string): string => {
  if (!t) return t;
  let result = t.toLowerCase();
  result = result.charAt(0).toUpperCase() + result.slice(1);
  result = result.replace(
    /([.!?]\s*)([a-záéíóúñ])/g,
    (_, punct, letter) => punct + letter.toUpperCase(),
  );
  return result;
};

/**
 * Format log text with highlighted names and camera badges.
 * @param text - Raw log text
 * @param participants - Array of participant objects with name, alias
 * @param options - Optional config: sentenceCase applies sentence case to plain text
 * @returns React nodes with formatted text
 */
export function formatLogText(
  text: string,
  participants: any[] = [],
  options?: { sentenceCase?: boolean },
): React.ReactNode {
  if (!text) return "";

  // Some callers pass values from loosely-typed shapes (log.text || log.title)
  // where the field can be a non-string (Plate node, object). Bail out \u2014 the
  // caller is responsible for unwrapping; coercing here would render
  // "[object Object]" as a styled chip via the [...] pattern below.
  if (typeof text !== "string") return "";

  // Clean BOM characters
  const cleanText = text.replace(/\ufeff/g, "");

  // Combined pattern:
  // Group 1: @N-NAME mentions
  // Group 2: [BRACKET] tags
  // Group 3: Camera references - all variants:
  //   &amp;Cam X, &Cam X, cam X, CAM X, &amp;a10, &a10, &amp;l01, &l01, etc.
  const combinedPattern =
    /(@\d+-[A-ZÁÉÍÓÚÑ"']+(?:\s[A-ZÁÉÍÓÚÑ"']+)?)|(\[.*?\])|((?:&amp;|&)[Cc]am\s*[A-Za-z]?\d+|[Cc][Aa][Mm]\s+[A-Za-z]?\d+|(?:&amp;|&)[A-Za-el]\d{2,3})/gi;

  const parts = cleanText.split(combinedPattern).filter((p) => p !== undefined && p !== "");
  // Build clean participant name tokens for matching within text
  const nameTokens: string[] = [];
  participants.forEach((p: any) => {
    const cleanName = (p.name || "").replace(/^\d+-/, "").trim();
    if (
      cleanName &&
      cleanName.length > 3 &&
      !/^OTF_/i.test(cleanName) &&
      !/^MIC_/i.test(cleanName)
    ) {
      nameTokens.push(cleanName);
    }
    const cleanAlias = (p.alias || "").replace(/^\d+-/, "").trim();
    if (
      cleanAlias &&
      cleanAlias.length > 3 &&
      !/^OTF_/i.test(cleanAlias) &&
      !/^MIC_/i.test(cleanAlias)
    ) {
      nameTokens.push(cleanAlias);
    }
  });
  const nameLower = new Set(nameTokens.map((n) => n.toLowerCase()));

  // Build a secondary regex for inline name matching (not covered by @mentions)
  let nameRegex: RegExp | null = null;
  if (nameTokens.length > 0) {
    const sorted = [...nameTokens].sort((a, b) => b.length - a.length);
    const escaped = sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    nameRegex = new RegExp(`(${escaped.join("|")})`, "gi");
  }

  // Also detect "N-name" patterns in text like "17-carlos salcido" → strip prefix
  const numPrefixPattern = /\b(\d{1,3})-([a-záéíóúñA-ZÁÉÍÓÚÑ]{2,}(?:\s[a-záéíóúñA-ZÁÉÍÓÚÑ]+)*)/gi;

  // Normalize camera text to "CAM XXX" format
  const normalizeCam = (part: string): string => {
    let camName = part
      .replace(/^(&amp;|&)?/i, "") // Remove & or &amp;
      .replace(/^[Cc]am\s*/i, "") // Remove Cam prefix
      .trim();
    return "CAM " + camName.toUpperCase();
  };

  // Test if part is a camera reference. Must mirror `combinedPattern` group 3
  // exactly (anchored with ^…$) so it only matches real camera refs like
  // "&cam a01", "cam a01" o "&a01". Sin el ancla al sufijo numérico, el viejo
  // /^[Cc]am/ matcheaba cualquier texto que empezara con "cam" (camila,
  // cambio, cámara…) y se tragaba TODO el texto del log como un chip naranja.
  const isCamRef = (part: string): boolean => {
    return (
      /^(?:&amp;|&)[Cc]am\s*[A-Za-z]?\d+$/i.test(part) ||
      /^[Cc][Aa][Mm]\s+[A-Za-z]?\d+$/i.test(part) ||
      /^(?:&amp;|&)?[A-Za-el]\d{2,3}$/i.test(part)
    );
  };

  // Process a plain text segment for inline names, "N-name" prefixes, and cameras
  const processSegment = (segment: string, baseKey: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];

    // Strip "N-name" patterns (e.g., "17-carlos salcido" → "carlos salcido")
    let processed = segment.replace(numPrefixPattern, (_, _num, name) => name);

    // Split by names if we have any
    if (nameRegex) {
      const nameParts = processed.split(nameRegex).filter((p) => p !== "");
      nameParts.forEach((np, ni) => {
        if (nameLower.has(np.toLowerCase())) {
          result.push(
            <span key={`${baseKey}_n${ni}`} style={nameStyle}>
              {np.toUpperCase()}
            </span>,
          );
        } else {
          // Check for cameras in non-name text
          result.push(...processCamerasInText(np, `${baseKey}_c${ni}`));
        }
      });
    } else {
      // No participant names, just check for cameras
      result.push(...processCamerasInText(processed, baseKey));
    }

    return result;
  };

  // Process camera references within text (secondary pass for inline cameras)
  const processCamerasInText = (txt: string, baseKey: string): React.ReactNode[] => {
    const camRegex = /(?:&amp;|&)?(?:cam\s*)?([a-el])(\d{2,3})/gi;
    const result: React.ReactNode[] = [];
    let lastIdx = 0;
    let match;

    while ((match = camRegex.exec(txt)) !== null) {
      if (match.index > lastIdx) {
        const before = txt.substring(lastIdx, match.index);
        result.push(options?.sentenceCase ? toSentenceCase(before) : before);
      }
      const camLabel = `CAM ${match[1].toUpperCase()}${match[2]}`;
      result.push(
        <span key={`${baseKey}_${match.index}`} style={camBadgeStyle}>
          {camLabel}
        </span>,
      );
      lastIdx = match.index + match[0].length;
    }

    if (lastIdx < txt.length) {
      const remainder = txt.substring(lastIdx);
      result.push(options?.sentenceCase ? toSentenceCase(remainder) : remainder);
    }

    return result.length > 0 ? result : [options?.sentenceCase ? toSentenceCase(txt) : txt];
  };

  return parts.map((part, i) => {
    // @N-NAME mention → bold white, strip prefix
    if (/^@\d+-/.test(part)) {
      const name = part.replace(/^@\d+-/, "").trim();
      return (
        <span key={`m${i}`} style={nameStyle}>
          {name.toUpperCase()}
        </span>
      );
    }

    // [BRACKET] tag → bold white uppercase
    if (part.startsWith("[") && part.endsWith("]")) {
      return (
        <span
          key={`b${i}`}
          style={{
            color: "rgba(255, 255, 255, 0.8)",
            fontSize: "1em",
            fontWeight: 900,
            margin: "0 2px",
            textTransform: "uppercase",
          }}
        >
          {part.slice(1, -1)}
        </span>
      );
    }

    // Camera reference (matched by main pattern)
    if (isCamRef(part)) {
      const camLabel = normalizeCam(part);
      return (
        <span key={`cam${i}`} style={camBadgeStyle}>
          {camLabel}
        </span>
      );
    }

    // Plain text → process for inline names and cameras
    return <React.Fragment key={`t${i}`}>{processSegment(part, `s${i}`)}</React.Fragment>;
  });
}
